// src/pages/nba/NbaProjections.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
/* ----------------------------- CONFIG -------------------------------- */
const SOURCE = "/data/nba/latest/projections.json";
const META = "/data/nba/latest/meta.json";
const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};
// NBA team logos from /public/logos/nba/XXX.png
const NBA_LOGO_BASE = "/logos/nba";
const TEAM_ABBR_MAP = {
  NOH: "NOP",
  NOP: "NOP",
  NOR: "NOP",
  CHO: "CHA",
  CHA: "CHA",
  PHO: "PHX",
  PHX: "PHX",
  GSW: "GSW",
  GS: "GSW",
  SAS: "SAS",
  SA: "SAS",
  UTA: "UTA",
  UTH: "UTA",
  NY: "NYK",
  NYK: "NYK",
  NJN: "BKN",
  BRK: "BKN",
  BKN: "BKN",
  WAS: "WAS",
  WSH: "WAS",
  LAC: "LAC",
  LAL: "LAL",
};
const fixAbbr = (t) =>
  TEAM_ABBR_MAP[String(t || "").toUpperCase()] ||
  String(t || "").toUpperCase();
const teamLogo = (abbr) => `${NBA_LOGO_BASE}/${fixAbbr(abbr)}.png`;
/* --------------------------- COLUMN DEFS ------------------------------ */
const COLS_BASE = [
  {
    key: "player",
    label: "Player",
    type: "text",
    w: "min-w-[12rem] md:min-w-[14rem]",
  },
];
const COLS_TEAM_OPP = [
  { key: "team", label: "Team", type: "text" },
  { key: "opp", label: "Opp", type: "text" },
];
const COLS_STATS = [
  { key: "minutes", label: "Min", type: "num1" },
  { key: "pts", label: "PTS", type: "num1" },
  { key: "reb", label: "REB", type: "num1" },
  { key: "ast", label: "AST", type: "num1" },
  { key: "stl", label: "STL", type: "num1" },
  { key: "blk", label: "BLK", type: "num1" },
  { key: "tov", label: "TOV", type: "num1" },
  { key: "tpm", label: "3PM", type: "num1" },
];
const DK_POS_SAL = [
  { key: "dk_pos", label: "DK Pos", type: "text" },
  { key: "dk_sal", label: "DK Sal", type: "money" },
];
const DK_METRICS = [
  { key: "dk_proj", label: "DK Proj", type: "num1" },
  { key: "dk_val", label: "DK Val", type: "num1" },
  { key: "dk_ppm", label: "DK PPM", type: "num2" },
  { key: "dk_pown", label: "DK pOWN%", type: "pct" },
  { key: "dk_rtg", label: "DK Rtg", type: "num1-force" },
];
const FD_POS_SAL = [
  { key: "fd_pos", label: "FD Pos", type: "text" },
  { key: "fd_sal", label: "FD Sal", type: "money" },
];
const FD_METRICS = [
  { key: "fd_proj", label: "FD Proj", type: "num1" },
  { key: "fd_val", label: "FD Val", type: "num1" },
  { key: "fd_ppm", label: "FD PPM", type: "num2" },
  { key: "fd_pown", label: "FD pOWN%", type: "pct" },
  { key: "fd_rtg", label: "FD Rtg", type: "num1-force" },
];
function buildColumns(site) {
  if (site === "dk") {
    return [
      ...COLS_BASE,
      DK_POS_SAL[0],
      ...COLS_TEAM_OPP,
      DK_POS_SAL[1],
      ...COLS_STATS,
      ...DK_METRICS,
    ];
  }
  if (site === "fd") {
    return [
      ...COLS_BASE,
      FD_POS_SAL[0],
      ...COLS_TEAM_OPP,
      FD_POS_SAL[1],
      ...COLS_STATS,
      ...FD_METRICS,
    ];
  }
  return [
    ...COLS_BASE,
    DK_POS_SAL[0],
    FD_POS_SAL[0],
    ...COLS_TEAM_OPP,
    DK_POS_SAL[1],
    FD_POS_SAL[1],
    ...COLS_STATS,
    ...DK_METRICS,
    ...FD_METRICS,
  ];
}
/* ------------------------------ HELPERS ------------------------------- */
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt0 = (v) => (num(v) === null ? "" : num(v).toLocaleString());
const fmt1 = (v) => (num(v) === null ? "" : num(v).toFixed(1));
const fmt2 = (v) => (num(v) === null ? "" : num(v).toFixed(2));
/** Keys that we ALWAYS treat as 0–100 percentages (never 0–1 fractions). */
const FORCE_PERCENT_KEYS = new Set(["dk_pown", "fd_pown"]);
/** Robust percent formatter with auto scale + hard overrides for ownership. */
function makePctFormatter(scaleMap) {
  return (v, key) => {
    const n = num(v);
    if (n === null) return "";
    if (FORCE_PERCENT_KEYS.has(key)) {
      const clamped = Math.max(0, Math.min(100, n));
      return `${clamped.toFixed(1)}%`;
    }
    const detected = scaleMap[key] || "percent";
    const inferred =
      n > 0 && n <= 1
        ? "fraction"
        : n > 1 && n <= 100
        ? "percent"
        : detected;
    let pct = inferred === "fraction" ? n * 100 : n;
    pct = Math.max(0, Math.min(100, pct));
    return `${pct.toFixed(1)}%`;
  };
}
function detectPctScales(rows, pctKeys) {
  const map = {};
  for (const k of pctKeys) {
    if (FORCE_PERCENT_KEYS.has(k)) {
      map[k] = "percent";
      continue;
    }
    let lt1 = 0,
      btw1_100 = 0;
    for (const r of rows) {
      const n = num(r[k]);
      if (n == null) continue;
      if (n > 0 && n < 1) lt1++;
      if (n >= 1 && n <= 100) btw1_100++;
    }
    map[k] = btw1_100 >= lt1 ? "percent" : "fraction";
  }
  return map;
}
function normalizeRow(r) {
  return {
    player: r["Player"],
    team: r["Team"],
    opp: r["Opp"],
    minutes: r["Minutes"],
    pts: r["Points"],
    reb: r["Rebounds"],
    ast: r["Assists"],
    stl: r["Steals"],
    blk: r["Blocks"],
    tov: r["Turnovers"],
    tpm: r["3PM"],
    dk_pos: r["DK Pos"],
    dk_sal: r["DK Salary"],
    dk_proj: r["DK Proj"],
    dk_val: r["DK Val"],
    dk_ppm: r["DK PPM"],
    dk_pown: r["DK pOWN%"],
    dk_rtg: r["DK Rtg"],
    fd_pos: r["FD Pos"],
    fd_sal: r["FD Salary"],
    fd_proj: r["FD Proj"],
    fd_val: r["FD Val"],
    fd_ppm: r["FD PPM"],
    fd_pown: r["FD pOWN%"],
    fd_rtg: r["FD Rtg"],
  };
}
function useJson(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const arr = Array.isArray(j) ? j : j?.rows || j?.data || [];
        const normalized = arr.map(normalizeRow);
        if (alive) setRows(normalized);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { rows, loading, err };
}
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (r.ok) setMeta(await r.json());
      } catch {
        // ignore
      }
    })();
  }, [url]);
  return meta;
}
/* CSV */
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
function downloadCSV(rows, cols, fname = "nba_projections.csv") {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          const val = r[c.key];
          if (c.type === "money") return escapeCSV(fmt0(val));
          if (c.type === "num1" || c.type === "num1-force")
            return escapeCSV(fmt1(val));
          if (c.type === "num2") return escapeCSV(fmt2(val));
          if (c.type === "pct") {
            const n = num(val);
            if (n === null) return "";
            const forced = FORCE_PERCENT_KEYS.has(c.key);
            const scale =
              forced || n > 1
                ? "percent"
                : n > 0 && n < 1
                ? "fraction"
                : "percent";
            const pct = Math.max(
              0,
              Math.min(100, scale === "fraction" ? n * 100 : n)
            );
            return escapeCSV(`${pct.toFixed(1)}%`);
          }
          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* heatmap */
function dirForKey(k) {
  if (/_(proj|val|ppm|rtg)$/i.test(k)) return "higher";
  if (/_pown$/i.test(k)) return "lower";
  if (/(^minutes$|^pts$|^reb$|^ast$|^stl$|^blk$|^tpm$)/i.test(k)) return "higher";
  if (k === "tov") return "lower";
  return null;
}
/** Theme-aware text heat color: orange/yellow/green only */
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (
    v == null ||
    min == null ||
    max == null ||
    min === max ||
    !dir
  ) {
    return null;
  }
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const isDark = theme === "dark";
  // orange/red -> yellow -> green
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  } else {
    const u = (t - 0.5) / 0.5;
    const h = 50 + u * 70;
    const s = isDark ? 88 : 80;
    const l = isDark ? 62 + u * 8 : 44 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
}
/* -------------------- PRETTY TEAM MULTI-SELECT (themed) -------------------- */
function TeamsDropdown({ allTeams, selected, onChange, isDark }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const sel = new Set(selected);
  const toggleTeam = (t) => {
    const next = new Set(sel);
    next.has(t) ? next.delete(t) : next.add(t);
    onChange(Array.from(next));
  };
  const setAll = () => onChange([...allTeams]);
  const clear = () => onChange([]);
  // close on outside click / esc
  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  const btnBase =
    "h-8 px-3 rounded-md border text-xs font-medium";
  const btnClass = isDark
    ? `${btnBase} bg-slate-900/90 text-slate-100 border-slate-700/80 shadow`
    : `${btnBase} bg-white text-slate-800 border-slate-300 shadow-sm`;
  // z-30 so it sits above table header (z-10/20) but below nav (which is higher)
  const menuBase =
    "absolute left-0 top-full mt-2 w-[280px] max-w-[calc(100vw-4rem)] rounded-2xl border shadow-lg p-2 z-30";
  const menuClass = isDark
    ? `${menuBase} bg-slate-950 border-slate-800/90`
    : `${menuBase} bg-white border-slate-200`;
  const chipBase =
    "text-xs px-2 py-1 rounded-md border transition-colors";
  const selectAllClass = isDark
    ? `${chipBase} bg-slate-900 text-slate-100 border-slate-700 hover:bg-slate-800`
    : `${chipBase} bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200`;
  const clearClass = isDark
    ? "text-xs text-slate-400 hover:text-slate-100"
    : "text-xs text-slate-500 hover:text-slate-800";
  return (
    // wrapper also at z-30
    <div className="relative z-50">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={btnClass}
      >
        Teams ({selected.length}/{allTeams.length})
      </button>
      {open && (
        <div ref={menuRef} className={menuClass}>
          <div className="flex items-center justify-between mb-2">
            <button className={selectAllClass} onClick={setAll}>
              Select all
            </button>
            <button className={clearClass} onClick={clear}>
              Clear
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1 max-h-64 overflow-auto pr-1">
            {allTeams.map((t) => (
              <label
                key={t}
                className={
                  "flex items-center gap-1 text-xs " +
                  (isDark ? "text-slate-100" : "text-slate-800")
                }
              >
                <input
                  type="checkbox"
                  className={isDark ? "accent-sky-500" : "accent-blue-600"}
                  checked={sel.has(t)}
                  onChange={() => toggleTeam(t)}
                />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
/* ------------------------------- PAGE --------------------------------- */
export default function NbaProjections() {
  const { rows, loading, err } = useJson(SOURCE);
  const meta = useMeta(META);
  const [site, setSite] = useState("both");
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  // Teams for dropdown
  const allTeams = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => fixAbbr(r.team)).filter(Boolean))
      ).sort(),
    [rows]
  );
  const [teamsSel, setTeamsSel] = useState(allTeams);
  useEffect(() => {
    if (allTeams.length && (!teamsSel || teamsSel.length === 0)) {
      setTeamsSel(allTeams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTeams]);
  // Site-aware position chips
  const posMap = {
    dk: ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"],
    fd: ["PG", "SG", "SF", "PF", "C", "UTIL"],
    both: ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"],
  };
  const [posSel, setPosSel] = useState(posMap.both);
  useEffect(() => {
    setPosSel(posMap[site]);
  }, [site]);
  const columns = useMemo(() => buildColumns(site), [site]);
  // Detect percent scale per column
  const pctKeys = useMemo(
    () => columns.filter((c) => c.type === "pct").map((c) => c.key),
    [columns]
  );
  const pctScaleMap = useMemo(
    () => detectPctScales(rows, pctKeys),
    [rows, pctKeys]
  );
  const fmtPct1 = useMemo(
    () => makePctFormatter(pctScaleMap),
    [pctScaleMap]
  );
  // ---------- SITE-SPECIFIC POSITION MATCHING ----------
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const teamSet = new Set(teamsSel);
    const posSet = new Set(posSel);
    const split = (s) =>
      String(s || "")
        .split(/[\/,]/)
        .map((x) => x.trim())
        .filter(Boolean);
    const matchesPosForSite = (pos, siteKey, dkPos, fdPos) => {
      const dk = split(dkPos);
      const fd = split(fdPos);
      const pool =
        siteKey === "dk" ? dk : siteKey === "fd" ? fd : [...dk, ...fd];
      if (pos === "UTIL") return true;
      if (pos === "G")
        return pool.some((p) => p === "PG" || p === "SG");
      if (pos === "F")
        return pool.some((p) => p === "SF" || p === "PF");
      return pool.includes(pos);
    };
    return rows.filter((r) => {
      if (teamSet.size && !teamSet.has(fixAbbr(r.team))) return false;
      // site-specific position gating
      if (posSet.size) {
        const hit = Array.from(posSet).some((p) =>
          matchesPosForSite(p, site, r.dk_pos, r.fd_pos)
        );
        if (!hit) return false;
      }
      if (!needle) return true;
      const bag = `${r.player ?? ""} ${r.team ?? ""} ${r.opp ?? ""} ${
        r.dk_pos ?? ""
      } ${r.fd_pos ?? ""}`.toLowerCase();
      return bag.includes(needle);
    });
  }, [rows, q, teamsSel, posSel, site]);
  /* sorting */
  const defaultKey = site === "fd" ? "fd_proj" : "dk_proj";
  const [sort, setSort] = useState({ key: defaultKey, dir: "desc" });
  useEffect(
    () =>
      setSort({
        key: site === "fd" ? "fd_proj" : "dk_proj",
        dir: "desc",
      }),
    [site]
  );
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = num(a[key]);
      const bv = num(b[key]);
      if (av == null && bv == null)
        return (
          String(a[key] ?? "").localeCompare(String(b[key] ?? "")) *
          sgn
        );
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * sgn;
    });
    return arr;
  }, [filtered, sort]);
  // Sticky widths
  const playerThRef = useRef(null);
  const [playerColWidth, setPlayerColWidth] = useState(0);
  useLayoutEffect(() => {
    const calc = () => {
      if (playerThRef.current) {
        setPlayerColWidth(
          playerThRef.current.getBoundingClientRect().width
        );
      }
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [columns, sorted]);
  // heat min/max for visible rows
  const heatStats = useMemo(() => {
    const stats = {};
    if (!sorted.length) return stats;
    for (const c of columns) {
      const k = c.key;
      const dir = dirForKey(k);
      if (!dir) continue;
      let min = Infinity,
        max = -Infinity;
      for (const r of sorted) {
        const v = num(r[k]);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (min !== Infinity && max !== -Infinity)
        stats[k] = { min, max, dir };
    }
    return stats;
  }, [sorted, columns]);
  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";
  const textSz = "text-[11px] md:text-[12px]";
  const updatedStamp = useMemo(() => {
    const raw = meta?.last_updated || meta?.tags?.last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime())
      ? null
      : d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
  }, [meta]);
  /* ---------- Themed wrappers & classes ---------- */
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  // Keep header fully opaque so sticky header/columns don't look transparent while scrolling.
  const headerBgClass = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-slate-50 text-slate-700";
  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-50";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  // Keep zebra rows fully opaque so sticky Player cell doesn't appear see-through.
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";
  const tableTextClass = isDark
    ? "text-slate-100"
    : "text-slate-900";
  const stickyCellBg = isDark ? "bg-slate-950" : "bg-white";
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        {/* Top header row: title + rows + updated + theme toggle */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">NBA — DFS Projections</h1>
            <span className={"text-xs italic " + (isDark ? "text-slate-400" : "text-slate-500")}>Updated: {updatedStamp ?? "—"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {/* theme toggle pill */}
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark
                  ? "bg-slate-900/90 ring-slate-700/80"
                  : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>
        <section aria-label="Site" className={cardClass + " mb-4"}>
          <div className="px-4 py-3">
            <div className={"mb-1 text-[11px] font-semibold " + (isDark ? "text-slate-400" : "text-slate-500")}>Site</div>
            {/* site toggle */}
            <div className={"inline-flex items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {["dk", "fd", "both"].map((k) => (
                <button
                  key={k}
                  onClick={() => setSite(k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                  }`}
                  title={`Show ${SITES[k].label}`}
                >
                  {k !== "both" ? (
                    <img
                      src={SITES[k].logo}
                      alt={SITES[k].label}
                      className="w-4 h-4"
                    />
                  ) : null}
                  <span>{SITES[k].label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
        <section aria-label="Projections" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">Player Projections <span className={"ml-2 text-xs font-normal " + (isDark ? "text-slate-400" : "text-slate-500")}>{!loading && !err ? `${sorted.length.toLocaleString()} players` : ""}</span></h2>
            {/* export */}
            <button
              className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400"
              onClick={() => downloadCSV(sorted, columns)}
            >
              Export CSV
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            {/* Position chips */}
            <div className={"flex w-full flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {posMap[site].map((p) => {
                const active = posSel.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() =>
                      setPosSel((prev) =>
                        prev.includes(p)
                          ? prev.filter((x) => x !== p)
                          : [...prev, p]
                      )
                    }
                    className={`h-9 px-2.5 border-b-2 text-xs font-semibold transition ${
                      active
                        ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                        : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                    }`}
                    title="Toggle position"
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            {/* Teams dropdown */}
            <div className="ml-1">
              <TeamsDropdown
                allTeams={allTeams}
                selected={teamsSel}
                onChange={setTeamsSel}
                isDark={isDark}
              />
            </div>
            {/* search */}
            <input
              className={
                "h-8 w-full sm:ml-auto sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
              }
              placeholder="Search player / team / opp…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {/* Scrollable wrapper: internal scroll */}
          <div className="overflow-auto max-h-[70vh] rounded-b-2xl">
            <table
              className={`w-full border-separate ${textSz} ${tableTextClass}`}
              style={{ borderSpacing: 0 }}
            >
              {/* ↓ table header: lower z so TeamsDropdown (z-30) can sit above */}
              {/* Use per-cell sticky headers (more reliable with horizontal scroll + sticky left column). */}
              <thead className={`${headerBgClass}`}>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      ref={c.key === "player" ? playerThRef : undefined}
                      className={`${header} sticky top-0 whitespace-nowrap cursor-pointer select-none ${
                        c.w || ""
                      } ${headerBgCell} ${
                        c.key === "player"
                          ? "left-0 z-30 shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.45)]"
                          : "z-10"
                      }`}
                      onClick={() =>
                        setSort((prev) =>
                          prev.key !== c.key
                            ? { key: c.key, dir: "desc" }
                            : {
                                key: c.key,
                                dir:
                                  prev.dir === "desc" ? "asc" : "desc",
                              }
                        )
                      }
                      title="Click to sort"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        <span
                          className={`text-gray-400 ${
                            sort.key === c.key ? "" : "opacity-40"
                          }`}
                        >
                          {sort.key === c.key
                            ? sort.dir === "desc"
                              ? "▼"
                              : "▲"
                            : "▲"}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      className={`${cell} ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                      colSpan={columns.length}
                    >
                      Loading…
                    </td>
                  </tr>
                )}
                {err && (
                  <tr>
                    <td
                      className={`${cell} text-red-500`}
                      colSpan={columns.length}
                    >
                      Failed to load: {err}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  sorted.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;
                    const rowBgSolid = isDark
                      ? (i % 2 === 0 ? "bg-slate-950" : "bg-slate-900")
                      : (i % 2 === 0 ? "bg-white" : "bg-gray-50");
                    return (
                      <tr
                      key={`${r.player}-${i}`}
                      className={
                        i % 2 === 0 ? rowOddClass : rowEvenClass
                      }
                    >
                      {columns.map((c) => {
                        const k = c.key;
                        if (k === "player") {
                          return (
                            <td
                              key={k}
                              className={`px-2 py-1 text-left sticky left-0 z-10 ${rowBgSolid} shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.45)]`}
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  src={teamLogo(r.team)}
                                  alt=""
                                  className="w-4 h-4 rounded-sm object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.visibility =
                                      "hidden";
                                  }}
                                />
                                <span
                                  className="whitespace-nowrap"
                                  title={r.player}
                                >
                                  {r.player}
                                </span>
                              </div>
                            </td>
                          );
                        }
                        if (k === "team") {
                          return (
                            <td key={k} className={`px-2 py-1 text-center`}>
                              {fixAbbr(r.team)}
                            </td>
                          );
                        }
                        const stat = heatStats[k];
                        const vNum = num(r[k]);
                        const textColor = stat
                          ? heatTextColor(
                              stat.min,
                              stat.max,
                              vNum,
                              stat.dir,
                              theme
                            )
                          : null;
                        let val = r[k];
                        if (c.type === "num1" || c.type === "num1-force") val = fmt1(val);
                        if (c.type === "num2") val = fmt2(val);
                        if (c.type === "money") val = fmt0(val);
                        if (c.type === "pct") val = fmtPct1(r[k], k);
                        return (
                          <td
                            key={k}
                            className={`${cell} ${c.type === "text" ? "" : "tabular-nums"}`}
                          >
                            <span
                              style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                            >
                              {val ?? ""}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                {!loading && !err && sorted.length === 0 && (
                  <tr>
                    <td
                      className={`${cell} ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                      colSpan={columns.length}
                    >
                      No rows match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
